// MIRROR of apps/backend/api/constants/source_type_registry.py.
// Drift between this file and the backend will silently misclassify
// existing data-source rows in the AllowlistBanner. The backend exposes
// the canonical set via GET /superadmin/source-types/registry — a CI
// drift-check (registry-mapping.test.ts) compares both.

// Structural shape — accepts both api/dataSources and types/dataSources DTOs.
export type DataSourceForRegistry = {
  connection_type: string;
  ds_key: string;
  ownership_mode: string;
};

// NOTE: `profile-config` is an ACCOUNT-ENTITLEMENT key only — it has no tile in
// the Add-data-source modal (segmentation is switched on for an existing
// BigQuery source, it is not a connector you add). It stays here because this
// list mirrors the backend's `SOURCE_TYPE_REGISTRY` 1:1 and the drift test
// compares both; dropping it would report false drift, not remove an entry.
export const SOURCE_TYPE_REGISTRY: ReadonlyArray<string> = [
  "meta",
  "google-ads",
  "ga4",
  "microsoft-ads",
  "exact-online",
  "brevo",
  "klaviyo",
  "shopify",
  "gsc",
  "woocommerce",
  "activecampaign",
  "verity-pixel",
  "mollie",
  "magento",
  "other-bq",
  "other-sql",
  "other-snowflake",
  "profile-config",
];

// Source types that exist but are not part of the default offering
// (ADR-0313). A null allowlist ("no restriction") still leaves these off; an
// account gets one only by naming it in an explicit allowlist. Mirrors
// PREVIEW_SOURCE_TYPES in the backend registry (drift-tested).
export const PREVIEW_SOURCE_TYPES: ReadonlyArray<string> = [
  "activecampaign",
  "verity-pixel",
  "mollie",
  "magento",
];

/**
 * Is `typeKey` activatable under this allowlist value? Same rule as the
 * backend's `source_type_enabled`: null enables every key except the preview
 * ones, a list enables exactly what it names.
 */
export function isSourceTypeAllowed(
  allowlist: ReadonlyArray<string> | null,
  typeKey: string,
  preview: ReadonlyArray<string> = PREVIEW_SOURCE_TYPES,
): boolean {
  if (allowlist === null) return !preview.includes(typeKey);
  return allowlist.includes(typeKey);
}

// Used by the list-page banner for `ownership_mode === "customer"` rows.
export const CONNECTION_TYPE_TO_REGISTRY_KEY: Record<string, string> = {
  BIGQUERY: "other-bq",
  SQL: "other-sql",
  SNOWFLAKE: "other-snowflake",
};

// Used by the list-page banner for managed/transform/semantics rows.
export const DS_KEY_TO_REGISTRY_KEY: Record<string, string> = {
  "facebook-ads": "meta",
  ga4: "ga4",
  "ga4-bq-export": "ga4",
  "google-ads": "google-ads",
  "brevo-email": "brevo",
  "klaviyo-email": "klaviyo",
  shopify: "shopify",
  "microsoft-ads": "microsoft-ads",
  gsc: "gsc",
  woocommerce: "woocommerce",
  "activecampaign-email": "activecampaign",
  "verity-pixel": "verity-pixel",
  "mollie": "mollie",
  "magento": "magento",
};

// MIRROR of backend DASHBOARD_TAG_TO_REGISTRY_KEY. Dashboard `tags` use a
// different label than the underlying ds_key for some families (the 2026-05-16
// rename moved dashboard tags facebook-ads → meta-ads while DataSource.ds_key
// stayed facebook-ads), so the dashboard data-source picker resolves by family.
export const DASHBOARD_TAG_TO_REGISTRY_KEY: Record<string, string> = {
  "meta-ads": "meta",
};

/**
 * Resolve a dashboard type-tag OR a DataSource ds_key to its registry family.
 * Fail-closed: returns null for anything unrecognized.
 */
export function resolveRegistryKey(
  tagOrDsKey: string | null | undefined,
): string | null {
  if (!tagOrDsKey) return null;
  return (
    DS_KEY_TO_REGISTRY_KEY[tagOrDsKey] ??
    DASHBOARD_TAG_TO_REGISTRY_KEY[tagOrDsKey] ??
    null
  );
}

/**
 * Resolve an existing DataSource row to a canonical registry-key.
 *
 * Returns `null` for rows that don't map to any known type — callers
 * (banner) treat unknown as "outside allowlist" so admins can surface
 * orphan rows.
 */
export function dataSourceToRegistryKey(
  ds: DataSourceForRegistry,
): string | null {
  if (ds.ownership_mode === "customer") {
    return (
      CONNECTION_TYPE_TO_REGISTRY_KEY[
        (ds.connection_type ?? "").toUpperCase()
      ] ?? null
    );
  }
  return DS_KEY_TO_REGISTRY_KEY[(ds.ds_key ?? "").toLowerCase()] ?? null;
}
